import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnChanges
} from '@angular/core';

import { FilterSorterCriterias } from '@shared/filters/entities/fetch-filters.response';
import { FilterApplier } from '@shared/flight-itinerary/entities/filters';
import { isNullOrUndefined } from '@app/util/util';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { getOrderCriteriaAux, mapSorterLabel } from '@flight-results/mappers/filters.mapper';
import { AirFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { RadioButtonAlign } from '@design-system/components/organisms/radio-button/entities/radio-button.types';
import {
  TypographyFontFamily, TypographyFontSize, TypographyColor
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-air-sorter-filter',
  templateUrl: './air-sorter-filter.component.html',
  styleUrls: ['./air-sorter-filter.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AirSorterFilterComponent implements OnChanges {

  @Input() individualFilter = false;
  @Input() departureCriteria: string;
  @Input() returnCriteria: string;
  @Input() sorterSelected: string;
  @Input() orderCriteriaSectorNumber: number;
  @Input() options: AirFilterVm;
  @Input() index = -1;
  @Input() orderDesc: boolean;
  @Input() isMobile = false;

  @Output() sortFlights: EventEmitter<FilterApplier> = new EventEmitter<FilterApplier>();

  public radioSelected: string;
  public collapsed: boolean;
  public isOpenBox = true;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  private readonly maxItemsOnViewPort = 5;

  constructor() {
    this.collapsed = true;
  }

  ngOnChanges(): void {
    this.updateRadioSelected(this.orderCriteriaSectorNumber);
  }

  get radioButtonAlign(): RadioButtonAlign {
    return this.isMobile ? RadioButtonAlign.Right : RadioButtonAlign.Left;
  }

  get collapseList(): boolean {
    return !this.individualFilter && this.orderedOptions?.length > this.maxItemsOnViewPort;
  }

  get orderedOptions(): FilterSorterCriterias[] {
    const _options = Object.assign([], this.options.value);
    _options.sort((a, b) =>
      a.OrderCriteria === SorterTypes.Recommended ? -1 : b.OrderCriteria === SorterTypes.Recommended ? 1 : 0
    );
    return _options;
  }

  public isChecked(option: string): boolean {
    return option === this.radioSelected;
  }

  public collapsibleListAction(): void {
    this.collapsed = !this.collapsed;
  }

  public getCodePlace(sorter: FilterSorterCriterias): string {
    return isNullOrUndefined(sorter.SectorNumber) || sorter.SectorNumber === 1 ?
      sorter.OrderCriteria === SorterTypes.Arrival ? this.returnCriteria : this.departureCriteria :
      sorter.OrderCriteria === SorterTypes.Arrival ? this.departureCriteria : this.returnCriteria;
  }

  public getSorterTitle(sorter: FilterSorterCriterias): string {
    return mapSorterLabel(sorter.OrderCriteria, sorter.SectorNumber, sorter.OrderDesc);
  }

  public changeSortOption(sorter: FilterSorterCriterias): void {
    if (sorter.OrderCriteriaAux !== this.sorterSelected) {
      this.sorterSelected = sorter.OrderCriteriaAux;
      this.sortFlightsAction(sorter.OrderCriteria, sorter?.SectorNumber, sorter?.OrderDesc);
    }
  }

  public toggleCheckboxOptions(): void {
    this.isOpenBox = !this.isOpenBox;
  }

  public sorterIcon(sorter: FilterSorterCriterias): Icon {
    switch (sorter.OrderCriteria) {
      case SorterTypes.Price:
        return Icon.MoneyIcon;
      case SorterTypes.Duration:
        return Icon.Clock;
      case SorterTypes.Recommended:
        return Icon.Check;
      case SorterTypes.Departure:
        return Icon.Flight1;
      case SorterTypes.Arrival:
        return Icon.Flight2;
      default:
        return Icon.Check;
    }
  }

  private updateRadioSelected(sectorNumber?: number): void {
    if (isNullOrUndefined(this.sorterSelected)) {
      this.sorterSelected = SorterTypes.Recommended;
    }
    this.radioSelected = !isNullOrUndefined(sectorNumber)
      ? getOrderCriteriaAux(this.sorterSelected as SorterTypes, sectorNumber, this.orderDesc)
      : this.sorterSelected;
  }

  private sortFlightsAction(value: string, sector: number, orderDesc: boolean): void {
    this.sortFlights.emit({ type: this.options.type, value, sector, orderDesc });
  }

}
